import { effectiveSellingPrice } from "@/lib/products";
export { uniformOverridePromotionCandidate } from "@/lib/price-inheritance";

export type VariantPriceMatrixRow = {
  id: string;
  sku: string | null;
  label: string;
  storedSellingPrice: number | null;
};

/** Empty override input means inherit the product base. */
export function seedOverrideInputs(rows: VariantPriceMatrixRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    out[row.id] = row.storedSellingPrice != null ? String(row.storedSellingPrice) : "";
  }
  return out;
}

export function parseOverrideInput(
  raw: string,
  productBase: number | null,
): { ok: true; stored: number | null } | { ok: false; error: string } {
  const t = raw.trim();
  if (t === "") return { ok: true, stored: null };
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, error: "Override prices must be zero or a positive amount." };
  }
  if (productBase != null && n === productBase) return { ok: true, stored: null };
  return { ok: true, stored: n };
}

export function buildVariantPriceSaveLines(
  rows: VariantPriceMatrixRow[],
  overrideInputs: Record<string, string>,
  productBase: number | null,
): { ok: true; lines: { variant_id: string; selling_price: number | null }[] } | { ok: false; error: string } {
  const lines: { variant_id: string; selling_price: number | null }[] = [];
  for (const row of rows) {
    const parsed = parseOverrideInput(overrideInputs[row.id] ?? "", productBase);
    if (parsed.ok === false) return { ok: false, error: parsed.error };
    lines.push({ variant_id: row.id, selling_price: parsed.stored });
  }
  return { ok: true, lines };
}

export function bulkClearOverrides(
  overrideInputs: Record<string, string>,
  selectedIds: string[],
): Record<string, string> {
  const next = { ...overrideInputs };
  for (const id of selectedIds) next[id] = "";
  return next;
}

export function bulkSetOverrideAmount(
  overrideInputs: Record<string, string>,
  selectedIds: string[],
  amount: string,
): Record<string, string> {
  const next = { ...overrideInputs };
  for (const id of selectedIds) next[id] = amount;
  return next;
}

/** Apply a dollar adjustment to the current effective price of selected rows. */
export function bulkAdjustSelected(
  rows: VariantPriceMatrixRow[],
  overrideInputs: Record<string, string>,
  selectedIds: string[],
  delta: number,
  productBase: number | null,
): { ok: true; inputs: Record<string, string> } | { ok: false; error: string } {
  if (!Number.isFinite(delta) || delta === 0) {
    return { ok: false, error: "Enter a non-zero adjustment amount." };
  }
  const selected = new Set(selectedIds);
  const next = { ...overrideInputs };
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    const currentStored = parseOverrideInput(overrideInputs[row.id] ?? "", productBase);
    if (currentStored.ok === false) return { ok: false, error: currentStored.error };
    const effective = effectiveSellingPrice(currentStored.stored, productBase);
    if (effective == null) {
      return { ok: false, error: "Set a product selling price before applying an adjustment." };
    }
    const nextAmt = Math.round((effective + delta) * 100) / 100;
    if (nextAmt < 0) {
      return { ok: false, error: "Adjustment would make a price negative." };
    }
    next[row.id] = productBase != null && nextAmt === productBase ? "" : String(nextAmt);
  }
  return { ok: true, inputs: next };
}

export function previewEffectivePrice(
  overrideInput: string,
  productBase: number | null,
): { amount: number | null; kind: "inherited" | "override" | "empty" } {
  const parsed = parseOverrideInput(overrideInput, productBase);
  if (!parsed.ok) return { amount: null, kind: "empty" };
  if (parsed.stored == null) {
    return { amount: productBase, kind: "inherited" };
  }
  return { amount: parsed.stored, kind: "override" };
}
